import React, { useState, useEffect, useCallback } from 'react';
import { alternativesAPI } from '@/api/alternatives';
import { useAuth } from '@/context/AuthContext';
import { showError, showSuccess } from '@/utils/errorHandler';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';
import { BLOCK_COLORS, BlockHeader, FieldLabel, MoneyInput, VendorSelect } from './FormHelpers';

const CURRENCY_OPTIONS = [
  { value: 'KZT', label: 'KZT' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
  { value: 'RUB', label: 'RUB' },
  { value: 'CNY', label: 'CNY' },
];

const STATUS_OPTIONS = [
  { value: 'рассмотрение', label: 'На рассмотрении', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'выбрано', label: 'Выбрано', color: 'bg-green-100 text-green-800' },
  { value: 'отклонено', label: 'Отклонено', color: 'bg-red-100 text-red-800' },
];

const EMPTY_FORM = {
  vendor: '',
  solution_name: '',
  indicative_price: '',
  currency: 'KZT',
  fx_rate: '1',
  status: 'рассмотрение',
  notes: '',
};

const AlternativeSolutions = ({ projectId }) => {
  const { user } = useAuth();
  const [alternatives, setAlternatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedAlternative, setSelectedAlternative] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const userRoleLower = user?.role?.toLowerCase();
  const canEdit = ['admin', 'pm', 'moderator', 'finance_admin', 'senior_manager'].includes(userRoleLower);
  const canDelete = ['admin', 'finance_admin'].includes(userRoleLower);

  const loadAlternatives = useCallback(async () => {
    try {
      setLoading(true);
      const data = await alternativesAPI.getByProject(projectId);
      setAlternatives(data);
    } catch (error) {
      showError(error, 'Ошибка загрузки альтернатив');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadAlternatives();
  }, [loadAlternatives]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculatePriceKzt = () => {
    const price = parseFloat(formData.indicative_price) || 0;
    const rate = parseFloat(formData.fx_rate) || 1;
    if (formData.currency === 'KZT') return price;
    return price * rate;
  };

  const openAddDialog = () => {
    setSelectedAlternative(null);
    setFormData(EMPTY_FORM);
    setShowAddDialog(true);
  };

  const openEditDialog = (alt) => {
    setSelectedAlternative(alt);
    setFormData({
      vendor: alt.vendor || '',
      solution_name: alt.solution_name || '',
      indicative_price: alt.indicative_price?.toString() || '',
      currency: alt.currency || 'KZT',
      fx_rate: alt.fx_rate?.toString() || '1',
      status: alt.status || 'рассмотрение',
      notes: alt.notes || '',
    });
    setShowAddDialog(true);
  };

  const openDeleteConfirm = (alt) => {
    setSelectedAlternative(alt);
    setShowDeleteConfirm(true);
  };

  const handleSave = async () => {
    if (!formData.vendor.trim() || !formData.solution_name.trim()) {
      showError(null, 'Заполните обязательные поля: Вендор и Название решения');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        project_id: projectId,
        vendor: formData.vendor.trim(),
        solution_name: formData.solution_name.trim(),
        indicative_price: parseFloat(formData.indicative_price) || 0,
        currency: formData.currency,
        fx_rate: parseFloat(formData.fx_rate) || 1,
        status: formData.status,
        notes: formData.notes.trim() || null,
      };

      if (selectedAlternative) {
        await alternativesAPI.update(selectedAlternative.id, payload);
        showSuccess('Альтернатива обновлена');
      } else {
        await alternativesAPI.create(payload);
        showSuccess('Альтернатива добавлена');
      }

      setShowAddDialog(false);
      loadAlternatives();
    } catch (error) {
      showError(error, 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedAlternative) return;

    try {
      setDeleting(true);
      await alternativesAPI.delete(selectedAlternative.id);
      showSuccess('Альтернатива удалена');
      setShowDeleteConfirm(false);
      setSelectedAlternative(null);
      loadAlternatives();
    } catch (error) {
      showError(error, 'Ошибка удаления');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(o => o.value === status);
    return option ? option : STATUS_OPTIONS[0];
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-5 flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="pt-5 space-y-4">
          <div className="flex items-center justify-between">
            <BlockHeader title="АЛЬТЕРНАТИВНЫЕ РЕШЕНИЯ" color={BLOCK_COLORS.alternatives || '#6366F1'} />
            {canEdit && (
              <Button
                size="sm"
                variant="outline"
                onClick={openAddDialog}
                className="flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Добавить
              </Button>
            )}
          </div>

          {alternatives.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Альтернативные решения не добавлены
            </div>
          ) : (
            <div className="space-y-3">
              {alternatives.map((alt) => {
                const statusInfo = getStatusBadge(alt.status);
                return (
                  <div
                    key={alt.id}
                    className="p-4 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900 truncate">
                            {alt.solution_name}
                          </span>
                          <Badge className={statusInfo.color}>
                            {statusInfo.label}
                          </Badge>
                        </div>
                        <div className="text-sm text-gray-600 mb-2">
                          Вендор: {alt.vendor}
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Цена:</span>{' '}
                            <span className="font-medium">
                              {formatCurrency(alt.indicative_price, alt.currency)}
                            </span>
                          </div>
                          {alt.currency !== 'KZT' && (
                            <>
                              <div>
                                <span className="text-gray-500">Курс:</span>{' '}
                                <span className="font-medium">{alt.fx_rate}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">В KZT:</span>{' '}
                                <span className="font-medium text-blue-600">
                                  {formatCurrency(alt.price_kzt_snapshot)}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                        {alt.notes && (
                          <div className="mt-2 text-sm text-gray-500 italic">
                            {alt.notes}
                          </div>
                        )}
                      </div>
                      {canEdit && (
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(alt)}
                            className="h-8 w-8 p-0"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {canDelete && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openDeleteConfirm(alt)}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {selectedAlternative ? 'Редактировать альтернативу' : 'Добавить альтернативу'}
            </DialogTitle>
            <DialogDescription>
              Укажите информацию об альтернативном решении
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-1">
              <FieldLabel required>Вендор</FieldLabel>
              <VendorSelect
                value={formData.vendor}
                onChange={(v) => handleInputChange('vendor', v)}
              />
            </div>

            <div className="space-y-1">
              <FieldLabel required>Название решения</FieldLabel>
              <Input
                value={formData.solution_name}
                onChange={(e) => handleInputChange('solution_name', e.target.value)}
                placeholder="Введите название решения"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <FieldLabel>Индикативная цена</FieldLabel>
                <MoneyInput
                  value={formData.indicative_price}
                  onChange={(v) => handleInputChange('indicative_price', v)}
                />
              </div>

              <div className="space-y-1">
                <FieldLabel>Валюта</FieldLabel>
                <Select
                  value={formData.currency}
                  onValueChange={(v) => handleInputChange('currency', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.currency !== 'KZT' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <FieldLabel>Курс к KZT</FieldLabel>
                  <Input
                    type="number"
                    value={formData.fx_rate}
                    onChange={(e) => handleInputChange('fx_rate', e.target.value)}
                    step="0.01"
                    min="0"
                  />
                </div>
                <div className="space-y-1">
                  <FieldLabel>Сумма в KZT</FieldLabel>
                  <div className="px-3 py-2 bg-blue-50 border rounded-md text-blue-700 font-medium">
                    {formatCurrency(calculatePriceKzt())}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-1">
              <FieldLabel>Статус</FieldLabel>
              <Select
                value={formData.status}
                onValueChange={(v) => handleInputChange('status', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <FieldLabel>Примечания</FieldLabel>
              <Textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Дополнительная информация"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddDialog(false)}
              disabled={saving}
            >
              Отмена
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить альтернативу?</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите удалить альтернативу "{selectedAlternative?.solution_name}"?
              Это действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Удаление...' : 'Удалить'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AlternativeSolutions;
